import { layeredBackground, layeredBackgroundSize } from './patterns.js'
// The scenery ridges are resolved by ThemeScene, which draws them — a theme
// only names one.
import { bands, disc, grid, stripes } from './themeSprites.js'

/**
 * Profile background presentation.
 *
 * Keyed by the same ids as `config/profileThemes.js`, which owns entitlement —
 * which backgrounds exist and what unlocks each. This file is pure styling and
 * only the SPA ever reads it, so it deliberately stays here rather than being
 * shipped over the wire as a second copy of the same list.
 *
 * ── The rarity ladder ────────────────────────────────────────────────────
 *
 * Each tier is the one below it plus one more idea, so what you're paying for
 * is legible from the picker without reading a word:
 *
 *   common     a colour
 *   rare       + a gradient and a tiled icon pattern
 *   epic       a *scene* — hard-edged composition and lighting
 *   legendary  + actors (things that fall, drift) and a shaped box
 *   mythical   + peekers and header architecture
 *
 * The jump from rare to epic is a change of kind, not degree, and that's
 * deliberate. Tiling stops at rare. Every tier used to be [soft gradient] +
 * [one uniformly tiled icon] at a higher alpha, which meant a mythical theme
 * was a rare theme shouting — the ladder had no rungs you could see. Above
 * rare a theme is now built from hard-edged bands, discs, rays and
 * silhouettes (see themeSprites.js), lit rather than blended, and populated
 * rather than stamped.
 *
 * Subjects draw on public-domain or genre-generic material only: an arcade
 * invader, a cactus, a voxel. None of them reproduce a mark,
 * a character or a copyrighted illustration. Read the note in AGENTS.md
 * before adding to the top tiers.
 *
 * ── The shape of a theme ─────────────────────────────────────────────────
 *
 *   base            The background, as CSS layers. Above rare these come
 *                   from the primitives in themeSprites.js.
 *   pattern         A tiled icon. rare and below only.
 *   backgroundSize  Set when a layer needs sizing the default `cover` breaks
 *                   (a grid or stripe layer wants `auto`).
 *   overlay         The translucent fill behind each card — this is what
 *                   carries legibility over a busy scene.
 *   text/accent     Foreground and small highlights.
 *   glow            Optional box-shadow on the cards.
 *   font            Optional display face for headings, loaded on demand.
 *
 * And, for a scene (epic and above):
 *
 *   scenery         A bottom-anchored silhouette: { ridge, color, height }.
 *   light           Lighting passes: shaft | pool | flicker | vignette.
 *   fall / drift    Actors that fall-and-rotate, or cross the panel.
 *   peek            Actors that rise from the horizon and duck back. Needs
 *                   `horizon`, and that horizon MUST sit below the profile
 *                   card — a peeker rising into the card's own box is
 *                   invisible, and fails silently.
 *   horizon         Where the ground starts, as a % of panel height.
 *   box             The card's own shape and material.
 *   header          Architecture on top of the card.
 *
 * Ownership is NOT checked here. It used to carry a `// TODO: Add check for
 * theme ownership here` and let anyone select anything; that check now lives
 * in `server/routes/profile.js`, where it can't be edited by the client.
 */

/**
 * A `base` is a list of layers, topmost first. Each is either a
 * `{ css, spec }` from a primitive or a bare CSS string.
 *
 * Splitting them apart is what lets the preview generator draw a theme on a
 * canvas: it reads the specs rather than trying to parse the CSS back into
 * shapes, which is what made the previous generator throw on anything past a
 * plain gradient.
 */
function composeBase(base) {
    const layers = Array.isArray(base) ? base : [base]
    return {
        css: layers.map(l => (typeof l === 'string' ? l : l.css)).join(','),
        specs: layers.map(l => (typeof l === 'string' ? { kind: 'css', css: l } : l.spec)),
    }
}

/** Assembles a theme so every entry declares the same things in the same order. */
function theme(spec) {
    const base = composeBase(spec.base)
    const colors = {
        primary: spec.primary,
        secondary: spec.secondary,
        accent: spec.accent,
        text: spec.text,
        overlay: spec.overlay,
        background: layeredBackground(base.css, spec.pattern),
    }
    if (spec.glow) colors.glow = spec.glow

    return {
        name: spec.name,
        rarity: spec.rarity,
        // An explicit size wins: a grid or stripe layer needs `auto`, which
        // neither the pattern sizer nor the stylesheet's `cover` can give it.
        backgroundSize: spec.backgroundSize || layeredBackgroundSize(spec.pattern),
        // The unmerged parts, for anything that has to draw the layers itself
        // rather than hand a CSS string to a browser.
        base: base.css,
        // The same layers as numbers, for anything that has to draw them
        // itself rather than hand a string to a browser.
        baseSpecs: base.specs,
        patternSpec: spec.pattern || null,
        colors,
        // Presentation beyond the background. All optional, all null for the
        // colour-and-pattern tiers, so ThemeScene can ask for any of them
        // without knowing which tier it's drawing.
        font: spec.font || null,
        scenery: spec.scenery || null,
        light: spec.light || null,
        fall: spec.fall || null,
        drift: spec.drift || null,
        peek: spec.peek || null,
        spinRays: spec.spinRays || null,
        horizon: spec.horizon || null,
        // A scene is composed for a band at the top of the panel, not for the
        // whole panel: the profile is several screens tall, and a horizon at
        // 56% of *that* puts the skyline halfway down the reward track. The
        // scene occupies `sceneHeight` and `ground` carries on beneath it.
        sceneHeight: spec.scenery || spec.peek || spec.light ? (spec.sceneHeight || 560) : null,
        ground: spec.ground || null,
        box: spec.box || null,
        header: spec.header || null,
    }
}

/** Pattern ink for a dark background, and for a light one. */
const ON_DARK = 'rgba(255,255,255,0.10)'
const ON_LIGHT = 'rgba(0,0,0,0.07)'

export const themes = {
    // ── Free ─────────────────────────────────────────────────────────────
    default: theme({
        name: 'Default', rarity: 'common',
        base: '#dbdbdb',
        primary: '#fafafa', secondary: '#c8c8c8', accent: '#121212',
        text: '#000000', overlay: 'rgba(255, 255, 255, 0.7)',
    }),

    mintGreen: theme({
        name: 'Mint Green', rarity: 'common',
        base: 'linear-gradient(160deg, #d6ffe8 0%, #8fe6b4 55%, #3CB371 100%)',
        primary: '#98FF98', secondary: '#3CB371', accent: '#2E8B57',
        text: '#08301c', overlay: 'rgba(240, 255, 246, 0.82)',
    }),

    // ── Common: a colour, and nothing else ───────────────────────────────

    yellow: theme({
        name: 'Yellow', rarity: 'common',
        base: '#ffd86b',
        primary: '#ffe9a3', secondary: '#f5b731', accent: '#7a5600',
        text: '#4a3300', overlay: 'rgba(255, 250, 235, 0.84)',
    }),

    rose: theme({
        name: 'Rose', rarity: 'common',
        base: '#f4b8c6',
        primary: '#ffd7e0', secondary: '#e08ba1', accent: '#8c3550',
        text: '#4a1220', overlay: 'rgba(255, 244, 247, 0.85)',
    }),

    charcoal: theme({
        name: 'Charcoal', rarity: 'common',
        base: '#2f333b',
        // `secondary` is the XP fill. It used to be the base colour exactly,
        // so the bar filled with the background and read as empty.
        primary: '#6b7280', secondary: '#15181d', accent: '#9aa3af',
        text: '#eef1f5', overlay: 'rgba(18, 20, 25, 0.74)',
    }),

    bone: theme({
        name: 'Bone', rarity: 'common',
        base: '#efe9dd',
        primary: '#fdfaf4', secondary: '#d8cfbd', accent: '#6b5f47',
        text: '#3a3226', overlay: 'rgba(255, 253, 248, 0.86)',
    }),

    denimBlue: theme({
        name: 'Denim Blue', rarity: 'common',
        base: '#3b5a86',
        primary: '#7d9bc4', secondary: '#2c4569', accent: '#e8edf5',
        text: '#eef3fb', overlay: 'rgba(22, 36, 58, 0.74)',
    }),

    sage: theme({
        name: 'Sage', rarity: 'common',
        base: '#9bb08c',
        primary: '#c3d4b6', secondary: '#7a8f6c', accent: '#3c4a33',
        text: '#26301f', overlay: 'rgba(248, 251, 245, 0.84)',
    }),


    // ── Rare: gradient + tiled pattern ───────────────────────────────────
    summerHaze: theme({
        name: 'Summer Haze', rarity: 'rare',
        base: 'linear-gradient(160deg, #ffd9a8 0%, #ffa8a0 45%, #ff6f91 100%)',
        pattern: { icon: 'leaf', color: ON_LIGHT, size: 76, angle: -22 },
        primary: '#FFB6C1', secondary: '#FF8C00', accent: '#C2410C',
        text: '#4a1d05', overlay: 'rgba(255, 244, 235, 0.82)',
    }),

    purpleCarnival: theme({
        name: 'Purple Carnival', rarity: 'rare',
        base: 'linear-gradient(135deg, #FF69B4 0%, #9370DB 50%, #4B0082 100%)',
        pattern: { icon: 'star', color: ON_DARK, size: 70, angle: -14 },
        primary: '#FF69B4', secondary: '#9370DB', accent: '#C77DFF',
        text: '#2b0a33', overlay: 'rgba(255, 240, 250, 0.84)',
    }),

    highRoller: theme({
        name: 'High Roller', rarity: 'rare',
        base: 'linear-gradient(150deg, #1c6b45 0%, #0f4c31 55%, #08301f 100%)',
        pattern: { icon: 'spade', color: 'rgba(255,255,255,0.09)', size: 74, angle: -20 },
        primary: '#4ecf8f', secondary: '#1c6b45', accent: '#f0d98a',
        text: '#eafff3', overlay: 'rgba(8, 40, 26, 0.76)',
    }),

    checkmate: theme({
        name: 'Checkmate', rarity: 'rare',
        base: 'linear-gradient(150deg, #f3f1ea 0%, #cfc9ba 50%, #6e6858 100%)',
        pattern: { icon: 'pawn', color: 'rgba(0,0,0,0.09)', size: 72, angle: -16 },
        primary: '#f3f1ea', secondary: '#6e6858', accent: '#2e2a22',
        text: '#241f18', overlay: 'rgba(252, 251, 246, 0.86)',
    }),


    royalFlush: theme({
        name: 'Royal Flush', rarity: 'rare',
        base: 'linear-gradient(150deg, #7a0f1e 0%, #40060f 55%, #120306 100%)',
        pattern: { icon: 'heart', color: 'rgba(255,255,255,0.09)', size: 72, angle: -20 },
        primary: '#e8556d', secondary: '#7a0f1e', accent: '#f0d98a',
        text: '#ffeaee', overlay: 'rgba(28, 5, 10, 0.76)',
    }),


    lagoon: theme({
        name: 'Lagoon', rarity: 'rare',
        base: 'linear-gradient(160deg, #bff0e6 0%, #6fc9c0 50%, #2b8a8a 100%)',
        pattern: { icon: 'wave', color: 'rgba(0,60,70,0.09)', size: 84, angle: 0 },
        primary: '#8fe0d5', secondary: '#2b8a8a', accent: '#14484f',
        text: '#0d3a3f', overlay: 'rgba(244, 255, 253, 0.84)',
    }),


    snowfall: theme({
        name: 'Snowfall', rarity: 'rare',
        base: 'linear-gradient(180deg, #e8f4ff 0%, #b8d8f0 50%, #7ba7cc 100%)',
        pattern: { icon: 'snowflake', color: 'rgba(255,255,255,0.45)', size: 70, angle: 0 },
        primary: '#d6ebfb', secondary: '#7ba7cc', accent: '#2c5a80',
        text: '#12354f', overlay: 'rgba(252, 254, 255, 0.86)',
    }),


    // ── Epic: a scene — hard edges and light, no tile ────────────────────
    moonlit: theme({
        name: 'Moonlit', rarity: 'epic',
        ground: '#0e1a38',
        base: [disc('#f4f1e0', 74, 20, 7),
            bands([['#0b1330', 30], ['#152449', 52], ['#1d3160', 74], ['#0e1a38', 100]])],
        light: [{ kind: 'pool', color: 'rgba(214,226,255,.42)', x: 74, y: 20, r: 30, seconds: 11 },
            { kind: 'vignette', color: 'rgba(4,8,24,.5)' }],
        primary: '#8fa6d8', secondary: '#152449', accent: '#d6e2ff',
        text: '#e8eeff', overlay: 'rgba(8, 15, 38, 0.80)',
        glow: '0 0 18px rgba(214, 226, 255, 0.28)',
    }),

    deepDive: theme({
        name: 'Deep Dive', rarity: 'epic',
        ground: '#020e18',
        base: [bands([['#0a3550', 22], ['#07283f', 46], ['#04192b', 70], ['#020e18', 100]])],
        light: [{ kind: 'shaft', color: 'rgba(150,220,255,.26)', width: '16%', seconds: 15 },
            { kind: 'shaft', color: 'rgba(150,220,255,.16)', width: '9%', seconds: 21 },
            { kind: 'vignette', color: 'rgba(0,6,14,.62)' }],
        primary: '#3fa8c8', secondary: '#07283f', accent: '#7fe0ff',
        text: '#dcf5ff', overlay: 'rgba(2, 16, 28, 0.80)',
        glow: '0 0 18px rgba(127, 224, 255, 0.26)',
    }),

    overclock: theme({
        name: 'Overclock', rarity: 'epic',
        ground: '#180907',
        base: [grid('rgba(255,120,60,.13)', 20, 1),
            bands([['#1c0c08', 34], ['#31120b', 58], ['#48170d', 78], ['#180907', 100]])],
        backgroundSize: 'auto, auto, cover',
        light: [{ kind: 'pool', color: 'rgba(255,110,50,.5)', x: 50, y: 62, r: 38, seconds: 5 },
            { kind: 'flicker', color: 'rgba(255,140,70,.07)' }],
        primary: '#ff8b52', secondary: '#31120b', accent: '#ffb066',
        text: '#ffe2d0', overlay: 'rgba(20, 8, 6, 0.82)',
        glow: '0 0 20px rgba(255, 139, 82, 0.38)',
    }),

    blueprint: theme({
        name: 'Blueprint', rarity: 'epic',
        ground: '#0b2745',
        font: 'Space Mono',
        // Two grid densities and no gradient at all — the one theme in the
        // catalogue with no colour ramp anywhere in it.
        base: [grid('rgba(190,225,255,.16)', 14, 1), grid('rgba(190,225,255,.30)', 70, 1),
            'linear-gradient(180deg,#10365e 0%,#0b2745 100%)'],
        backgroundSize: 'auto, auto, auto, auto, cover',
        light: [{ kind: 'shaft', color: 'rgba(140,210,255,.30)', width: '12%', seconds: 11 },
            { kind: 'vignette', color: 'rgba(4,18,36,.46)' }],
        primary: '#6fc3ff', secondary: '#0b2745', accent: '#6fc3ff',
        text: '#d6ecff', overlay: 'rgba(9, 32, 58, 0.84)',
        glow: '0 0 16px rgba(111, 195, 255, 0.30)',
    }),

    // ── Legendary: + actors, and a box with a shape of its own ───────────
    pinefall: theme({
        name: 'Pinefall', rarity: 'legendary',
        ground: '#3d3a34',
        font: 'Caveat',
        base: [bands([['#f2c185', 24], ['#d98e5a', 42], ['#8f5b46', 62], ['#3d3a34', 100]])],
        scenery: { ridge: 'pine', color: 'rgba(26,44,34,.82)', height: '46%' },
        fall: { sprite: 'leaf', color: '#e8a35c', count: 14, min: 9, max: 17, seconds: [9, 17], drift: 90 },
        light: [{ kind: 'shaft', color: 'rgba(255,220,160,.34)', width: '20%', seconds: 13 },
            { kind: 'vignette', color: 'rgba(30,26,18,.42)' }],
        box: { radius: '2px', frame: 'rgba(232,163,92,.5)', material: 'paper' },
        primary: '#e8a35c', secondary: '#8f5b46', accent: '#e8a35c',
        text: '#ffeccf', overlay: 'rgba(28, 26, 22, 0.82)',
        glow: '0 0 18px rgba(232, 163, 92, 0.32)',
    }),

    highNoon: theme({
        name: 'High Noon', rarity: 'mythical',
        ground: '#a8552f',
        font: 'Rye',
        base: [disc('#ffe08a', 66, 26, 9),
            bands([['#f6c56b', 30], ['#e89a4e', 46], ['#c9703c', 62], ['#a8552f', 100]])],
        scenery: { ridge: 'dune', color: 'rgba(140,84,40,.58)', height: '44%' },
        drift: { sprite: 'vulture', color: 'rgba(60,30,15,.62)', width: 44, top: '16%', seconds: 26 },
        // Glare off the sun, turning almost imperceptibly. Much weaker than
        // Sunburst's rays — there it's the subject, here it's the weather.
        spinRays: { color: 'rgba(255,240,180,.07)', count: 30, seconds: 150 },
        light: [{ kind: 'pool', color: 'rgba(255,240,180,.62)', x: 66, y: 26, r: 30, seconds: 8 },
            { kind: 'shaft', color: 'rgba(255,245,200,.26)', width: '30%', seconds: 16 },
            // Heat coming off the sand, low and slow.
            { kind: 'pool', color: 'rgba(255,190,120,.34)', x: 40, y: 92, r: 42, seconds: 13 },
            { kind: 'vignette', color: 'rgba(90,44,18,.34)' }],
        box: { clip: 'chamfer', frame: '#8a5a2c', radius: '0', material: 'wood' },
        // Cacti pop up from behind the cards rather than standing beside
        // them. Standing beside meant stretching one to the card's full
        // height, and a cactus is a squat, near-square drawing (24x26) —
        // columns were the only thing that shape of stretch ever suited,
        // which is why nothing stands beside a card any more.
        // The drawn wild-west set, self-coloured like the farm animals — no
        // `colors`, since the art carries its own.
        // More of them at the size they were drawn, rather than blown up to
        // fill the same box the farm animals do. A desert with several small
        // cacti in it reads better than one with two oversized ones.
        peek: { cast: ['cactus', 'cactusTall', 'snake'], perZone: 4, width: [26, 36] },
        // A ranch fence: two rails on posts, with the scene showing through
        // the gaps. Built from repeating gradients like Blockworld's turf —
        // the `plank` drawing stretched across ~930px was a brown smear with
        // two nails in it.
        header: {
            height: 26,
            railHeight: 100,
            inset: 6,
            rail:
                'linear-gradient(#8a5a2c, #8a5a2c) no-repeat 0 5px / 100% 6px,' +
                'linear-gradient(#8a5a2c, #8a5a2c) no-repeat 0 16px / 100% 6px,' +
                'repeating-linear-gradient(90deg, #6b4423 0 9px, transparent 9px 78px) no-repeat 0 0 / 100% 100%',
        },
        primary: '#e89a4e', secondary: '#a8552f', accent: '#c9703c',
        text: '#4a2410', overlay: 'rgba(255, 246, 230, 0.88)',
        glow: '0 0 18px rgba(201, 112, 60, 0.34)',
    }),

    skyline: theme({
        name: 'Skyline', rarity: 'legendary',
        ground: '#f0a06a',
        font: 'Orbitron',
        base: [disc('#ffb36b', 24, 30, 7),
            bands([['#1b1040', 26], ['#43206b', 44], ['#8a3f6e', 62], ['#d4715e', 78], ['#f0a06a', 100]])],
        scenery: { ridge: 'city', color: 'rgba(10,12,28,.90)', height: '42%' },
        drift: { sprite: 'cloud', color: 'rgba(255,255,255,.13)', width: 70, top: '22%', seconds: 38 },
        light: [{ kind: 'pool', color: 'rgba(255,180,110,.5)', x: 24, y: 30, r: 26, seconds: 11 },
            { kind: 'flicker', color: 'rgba(255,214,140,.10)' },
            { kind: 'vignette', color: 'rgba(8,6,24,.44)' }],
        box: { radius: '3px', frame: '#ff9f68', material: 'glass' },
        primary: '#ff9f68', secondary: '#43206b', accent: '#ff9f68',
        text: '#dfe6ff', overlay: 'rgba(10, 10, 26, 0.84)',
        glow: '0 0 22px rgba(255, 159, 104, 0.45)',
    }),

    sunburst: theme({
        name: 'Sunburst', rarity: 'legendary',
        ground: '#e0577f',
        font: 'Bungee',
        base: [disc('#ffd447', 50, 42, 13),
            bands([['#ffa63d', 46], ['#fb7a4e', 68], ['#e0577f', 100]])],
        // The rays are their own slowly-turning layer rather than part of the
        // base: a conic gradient can't be animated, but a box holding one can.
        spinRays: { color: 'rgba(255,255,255,.15)', count: 24, seconds: 90 },
        light: [{ kind: 'pool', color: 'rgba(255,230,150,.6)', x: 50, y: 42, r: 30, seconds: 6 }],
        box: { radius: '10px', frame: '#ffd447', material: 'glass' },
        primary: '#ffd447', secondary: '#fb7a4e', accent: '#ffd447',
        text: '#fff2d6', overlay: 'rgba(58, 14, 28, 0.80)',
        glow: '0 0 20px rgba(255, 212, 71, 0.40)',
    }),

    cyberpunk: theme({
        name: 'Cyberpunk', rarity: 'legendary',
        ground: '#0b0716',
        // Not Orbitron — Skyline already owns that, and two themes in the same
        // face read as one theme with two palettes.
        font: 'Rajdhani',
        // Overclock's bones: a grid over hard bands. Colder, and with the
        // grid in two colours so the lines read as circuitry rather than
        // graph paper.
        base: [grid('rgba(0,240,255,.13)', 22, 1), grid('rgba(255,60,180,.10)', 88, 1),
            bands([['#150b2e', 30], ['#241046', 54], ['#3a1259', 76], ['#0b0716', 100]])],
        backgroundSize: 'auto, auto, auto, auto, cover',
        // Three pools on different clocks, so the colour crossing the panel
        // never repeats the same way twice — the "more moving colour" this is
        // for. They're slow: at speed it strobes.
        light: [{ kind: 'pool', color: 'rgba(0,240,255,.42)', x: 20, y: 30, r: 40, seconds: 9 },
            { kind: 'pool', color: 'rgba(255,60,180,.40)', x: 82, y: 62, r: 42, seconds: 13 },
            { kind: 'pool', color: 'rgba(150,80,255,.32)', x: 50, y: 90, r: 46, seconds: 19 },
            { kind: 'shaft', color: 'rgba(0,240,255,.26)', width: '14%', seconds: 12 },
            { kind: 'flicker', color: 'rgba(255,60,180,.05)' },
            { kind: 'vignette', color: 'rgba(4,2,12,.54)' }],
        // Data, falling. Small and quick against the slow lights.
        fall: { sprite: 'mote', color: 'rgba(0,240,255,.75)', count: 22, min: 2, max: 5, seconds: [7, 14], drift: 30 },
        box: { clip: 'chamfer', frame: '#00f0ff', radius: '0', material: 'scan' },
        primary: '#00f0ff', secondary: '#3a1259', accent: '#ff3cb4',
        text: '#d6faff', overlay: 'rgba(9, 5, 22, 0.84)',
        glow: '0 0 22px rgba(0, 240, 255, 0.40)',
    }),

    // ── Mythical: + peekers and a header ─────────────────────────────────
    blockworld: theme({
        name: 'Blockworld', rarity: 'mythical',
        ground: '#4a6b28',
        font: 'Silkscreen',
        base: [bands([['#7ec0ff', 56], ['#6b9b3a', 66], ['#5a8331', 78], ['#4a6b28', 100]])],
        // The cast, not a seating plan: who turns up, and roughly how many per
        // card. Where each one stands is derived per zone by castPeekers().
        // No `zones` list, so they appear wherever a <peek-slot> is mounted —
        // adding a card to the page is enough to populate it.
        // perZone matches the cast size, so every card gets one of each. At 2
        // a zone showed half the cast, and because the shuffles are fixed the
        // shortfall wasn't spread evenly — the pig turned up once in eight,
        // in the footer, which is the least-looked-at strip on the page.
        peek: { cast: ['pig', 'chicken', 'horse', 'cow'], perZone: 4, width: [28, 40] },
        light: [{ kind: 'shaft', color: 'rgba(255,255,220,.30)', width: '22%', seconds: 14 },
            { kind: 'vignette', color: 'rgba(20,40,10,.30)' }],
        box: { clip: 'pixel', frame: '#3a5220', radius: '0', material: 'bevel' },
        // A grass block, edge on: the one thing in this theme's vocabulary
        // that is genuinely a horizontal band and so survives spanning the
        // card. Built from repeating gradients rather than a drawing, for the
        // same reason as the arcade sign — a texture tiles evenly at any
        // width, where a sprite could only be stretched. It was a `plank`,
        // which at ~930px was a green bar with two nails floating in it.
        // Flat colours and whole-pixel bands — a gradient or a speckle is the
        // opposite of what a voxel block looks like. Three equal 8px rows:
        // turf, the teeth biting into the dirt, and the dirt itself, so the
        // brown reads as the same course as the green above it.
        header: {
            height: 26,
            railHeight: 100,
            inset: 6,
            rail:
                'repeating-linear-gradient(90deg, #6b9b3a 0 9px, transparent 9px 18px) no-repeat 0 0 / 100% 18px,' +
                'linear-gradient(#6b9b3a, #6b9b3a) no-repeat 0 0 / 100% 10px,' +
                'linear-gradient(#7a5230, #7a5230)',
        },
        primary: '#8fd35a', secondary: '#4a6b28', accent: '#8fd35a',
        text: '#1c2a10', overlay: 'rgba(244, 252, 236, 0.88)',
        glow: '0 0 18px rgba(143, 211, 90, 0.36)',
    }),

    // Epic now, and without the columns. They flanked a pediment, and the
    // pair only worked if both read — the columns didn't, so the pediment was
    // holding up nothing. What's left is the marble and the light in it.
    pax: theme({
        name: 'Pax', rarity: 'epic',
        ground: '#a8946a',
        font: 'Cinzel',
        base: [bands([['#f7f2e4', 34], ['#e8dfc6', 52], ['#cbb98c', 72], ['#a8946a', 100]])],
        fall: { sprite: 'mote', color: 'rgba(255,250,225,.55)', count: 14, min: 3, max: 6, seconds: [16, 26], drift: 26 },
        light: [{ kind: 'shaft', color: 'rgba(255,250,225,.55)', width: '26%', seconds: 17 },
            { kind: 'pool', color: 'rgba(255,240,190,.5)', x: 50, y: 16, r: 34, seconds: 9 }],
        box: { radius: '3px', frame: '#e6dbbe', material: 'marble', shadow: '0 10px 26px rgba(60,45,20,.28)' },
        primary: '#e8dcbe', secondary: '#a8946a', accent: '#3f6f8f',
        text: '#3a3020', overlay: 'rgba(255, 253, 246, 0.90)',
        glow: '0 0 18px rgba(185, 166, 121, 0.36)',
    }),


    arcade: theme({
        name: 'Arcade', rarity: 'legendary',
        ground: '#12081f',
        font: 'Bitcount',
        base: [stripes('rgba(0,0,0,.35)', 2, 0),
            bands([['#170b2e', 34], ['#2a1050', 58], ['#3d1466', 78], ['#12081f', 100]])],
        backgroundSize: 'auto, cover',
        // On the cards, not the horizon. Arcade has no ridge — its horizon was
        // an invisible line partway down the panel, and the scene layer sits
        // behind the cards, so all three invaders were being drawn correctly
        // and occluded by the dashboard. Nobody ever saw them.
        peek: {
            cast: ['invader'],
            colors: ['#54f5b0', '#ff3cb4', '#8ff6ff'],
            perZone: 2,
            width: [22, 28],
        },
        light: [{ kind: 'pool', color: 'rgba(255,60,180,.5)', x: 50, y: 70, r: 40, seconds: 5 },
            { kind: 'flicker', color: 'rgba(140,255,255,.06)' }],
        box: { clip: 'chamfer', frame: '#ff3cb4', radius: '0', material: 'scan' },
        // The bulbs are the rail, not the drawing: a repeating gradient spaces
        // them evenly at any card width, where three drawn circles could only
        // be stretched or stranded in the middle.
        primary: '#ff3cb4', secondary: '#2a1050', accent: '#ff3cb4',
        text: '#8ff6ff', overlay: 'rgba(14, 6, 26, 0.84)',
        glow: '0 0 22px rgba(255, 60, 180, 0.42)',
    }),
}
